import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useUserStore } from '@/stores/modules/user';
import TrackingData from '@/shared/reportDataTracking';
import yxhJsBridge from '@/shared/yxhJsBridge';
import { throttle } from '@/shared/watchScroll.js';
import PrivacyDataBox from '@/components/privacy-data-box.vue';
import CreditFirstRetainPopup from '@/components/retain/credit-first-retain-popup.vue';
import CreditSecondRetainPopup from '@/components/retain/credit-second-retain-popup.vue';
import { usePageTime } from './usePageTime';

export function useCreditCommon() {
  const route = useRoute();
  const userStore = useUserStore();
  const { enterTime } = usePageTime();

  const agreed = ref(false);
  const oldScrollTop = ref(0);

  const setupScrollListener = () => {
    window.addEventListener(
      'scroll',
      throttle(
        function () {
          const scrollTop = window.pageYOffset;
          if (scrollTop > oldScrollTop.value) {
            TrackingData.trackMove({
              ...userStore.commonData,
              taskHandler: `${route.meta.trackingPgName}-move`,
            });
            window.removeEventListener('scroll', throttle);
          }
        },
        1000 * 60 * 60 * 24,
      ),
    );
  };

  onMounted(() => {
    setupScrollListener();
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', throttle);
  });

  return {
    agreed,
    enterTime,
    PrivacyDataBox,
  };
}

export function useFormTracking() {
  const route = useRoute();
  const userStore = useUserStore();
  const focusTimeMap = ref({});
  const focusDataMap = ref({});

  const onClickTrack = (str) => {
    TrackingData.trackClick({
      ...userStore.commonData,
      taskHandler: `${route.meta.trackingPgName}-${str}`,
    });
  };

  const onFocusTrack = (str) => {
    TrackingData.trackInput({
      ...userStore.commonData,
      taskHandler: `${route.meta.trackingPgName}-${str}_input`,
    });
    focusTimeMap.value[str] = new Date();
  };

  const onBlurTrack = (str, taskState = null) => {
    const obj = {
      ...userStore.commonData,
      taskHandler: `${route.meta.trackingPgName}-${str}_finish`,
      eventIntervalSpan: new Date() - focusTimeMap.value[str] - 200,
    };
    if (taskState !== null && taskState !== undefined) {
      obj.taskState = taskState;
    }
    setTimeout(() => {
      TrackingData.trackInput(obj);
    }, 200);
  };

  const onClosePopupTrack = (str, taskState) => {
    TrackingData.trackClick({
      ...userStore.commonData,
      taskHandler: `${route.meta.trackingPgName}-${str}`,
      taskState,
    });
  };

  return {
    focusTimeMap,
    focusDataMap,
    onClickTrack,
    onFocusTrack,
    onBlurTrack,
    onClosePopupTrack,
  };
}

export function useRetainPopup() {
  const route = useRoute();
  const userStore = useUserStore();
  const { enterTime } = usePageTime();
  const isShowFirstRetainPopup = ref(false);
  const isShowSecondRetainPopup = ref(false);

  const handleShowRetainPopup = () => {
    isShowFirstRetainPopup.value = true;
  };

  onMounted(() => {
    window.addEventListener('showRetainPopup', handleShowRetainPopup);
  });

  onUnmounted(() => {
    window.removeEventListener('showRetainPopup', handleShowRetainPopup);
  });

  const markSecondRetainPopupShown = () => {
    const key = new Date().toDateString();
    localStorage.setItem('hasShownSecondRetainPopup', key);
  };

  const hasShownSecondRetainPopupToday = () => {
    const key = new Date().toDateString();
    return localStorage.getItem('hasShownSecondRetainPopup') === key;
  };

  const showSecondRetainPopup = () => {
    if (hasShownSecondRetainPopupToday()) {
      isShowFirstRetainPopup.value = false;
      goAppHome();
    } else {
      isShowFirstRetainPopup.value = false;
      isShowSecondRetainPopup.value = true;
      markSecondRetainPopupShown();
    }
  };

  const goAppHome = async () => {
    isShowSecondRetainPopup.value = false;

    const formElement = document.querySelector('form, .van-form');
    if (formElement && formElement.__vue__) {
      const formComponent = formElement.__vue__;
      if (formComponent.validate) {
        try {
          await formComponent.validate();
        } catch (error) {
          console.log(error[0]?.name, 'error');
          const vaildStr = JSON.stringify({
            timeZone: new Date().getTime(),
            actionKey: 'input',
            pageTitle: route.meta?.trackingPgName,
            taskHandler: route.meta?.trackingPgName + '-miss_check',
            growArea: JSON.stringify({
              msg: error[0]?.name,
            }),
            ...userStore.commonData,
          });
          console.log(vaildStr, 'valid str');
          yxhJsBridge.reportWebData(vaildStr);
        }
      }
    }

    const pageEndStr = JSON.stringify({
      eventIntervalSpan: Date.now() - enterTime.value,
      timeZone: new Date().getTime(),
      actionKey: 'page_end',
      pageTitle: route.meta?.trackingPgName,
      taskHandler: route.meta?.trackingPgName + '-pageout',
      ...userStore.commonData,
    });
    console.log(pageEndStr, 'page end str');
    yxhJsBridge.reportWebData(pageEndStr);
    yxhJsBridge.closeHostWeb();
  };

  // simpler than goAppHome
  const goAppHomeSimple = () => {
    TrackingData.trackPageEnd({
      ...userStore.commonData,
      eventIntervalSpan: Date.now() - enterTime.value,
    });
    yxhJsBridge.jumpLocalHomePage();
  };

  return {
    isShowFirstRetainPopup,
    isShowSecondRetainPopup,
    showSecondRetainPopup,
    goAppHome,
    markSecondRetainPopupShown,
    hasShownSecondRetainPopupToday,
    goAppHomeSimple,
    CreditFirstRetainPopup,
    CreditSecondRetainPopup,
  };
}
